import React from 'react';
import { Star } from 'lucide-react';

/**
 * Star Rating Display Component
 * Shows 1-5 stars with filled/empty states
 */
export function StarRating({ rating, size = 'md', showCount = false, reviewCount = 0 }) {
  const sizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6'
  };

  const starSize = sizes[size] || sizes.md;
  const roundedRating = Math.round(rating || 0);

  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${starSize} ${
              star <= roundedRating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-none text-gray-300'
            }`}
          />
        ))}
      </div>
      {showCount && (
        <span className="text-sm text-muted-foreground ml-1">
          ({reviewCount})
        </span>
      )}
    </div>
  );
}

/**
 * Interactive Star Rating Input Component
 * Allows users to select a rating
 */
export function StarRatingInput({ rating, onChange, size = 'lg' }) {
  const [hoverRating, setHoverRating] = React.useState(0);

  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-8 w-8',
    xl: 'h-10 w-10'
  };

  const starSize = sizes[size] || sizes.lg;
  const displayRating = hoverRating || rating;

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="focus:outline-none transition-transform hover:scale-110"
          data-testid={`star-${star}`}
        >
          <Star
            className={`${starSize} cursor-pointer transition-colors ${
              star <= displayRating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-none text-gray-300 hover:text-yellow-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Rating Summary Component
 * Shows average rating with star display and review count
 */
export function RatingSummary({ averageRating, reviewCount, size = 'md' }) {
  if (!reviewCount || reviewCount === 0) {
    return (
      <span className="text-sm text-muted-foreground">No reviews yet</span>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <StarRating rating={averageRating} size={size} />
      <span className="font-semibold">{averageRating?.toFixed(1)}</span>
      <span className="text-muted-foreground">
        ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );
}

export default StarRating;
